import { MouseEvent, RefObject, useEffect, useRef, useState } from 'react'

interface useBottomSheetArgs {
  open: boolean
  onClose: (e?: MouseEvent<HTMLElement>) => void
  animationDuration?: number
  scrollableRef?: RefObject<HTMLElement | null>
}

export const useBottomSheet = ({ open, onClose, animationDuration = 350, scrollableRef }: useBottomSheetArgs) => {
  const [visible, setVisible] = useState(open)
  const [animationState, setAnimationState] = useState<'idle' | 'open' | 'closing'>('idle')
  const [swipeDirection, setSwipeDirection] = useState('')

  // 👇 Запоминаем scrollTop в момент НАЧАЛА свайпа
  const scrollTopAtSwipeStart = useRef(0)

  const onSwipeStart = () => {
    setSwipeDirection('')
    // Фиксируем позицию скролла до начала жеста
    scrollTopAtSwipeStart.current = scrollableRef?.current?.scrollTop ?? 0
  }

  const onSwipeMove = (pos: { y: number }) => {
    const scrollTop = scrollableRef?.current?.scrollTop ?? 0

    // Если контент проскроллен и пользователь тянет вверх — это скролл, не свайп на закрытие
    if (scrollTop > 0 && pos.y > 0) {
      setSwipeDirection('')

      return
    }

    setSwipeDirection(pos.y > 0 ? 'up' : 'down')
  }

  const onSwipeEnd = () => {
    const wasScrolledAtStart = scrollTopAtSwipeStart.current > 0

    // Закрываем ТОЛЬКО если:
    // 1. Свайп вверх
    // 2. В момент начала свайпа контент был в самом верху (scrollTop === 0)
    // Это значит пользователь изначально хотел закрыть модалку, а не скроллить
    if (swipeDirection === 'up' && !wasScrolledAtStart) {
      handleModalClose()
    }
  }

  const handleModalClose = (e?: MouseEvent<HTMLElement>) => {
    setAnimationState('closing')
    onClose(e)
  }

  useEffect(() => {
    if (open) {
      setVisible(true)
      setAnimationState('idle')

      const raf = requestAnimationFrame(() => setAnimationState('open'))

      return () => cancelAnimationFrame(raf)
    }

    setAnimationState('closing')

    const timer = setTimeout(() => setVisible(false), animationDuration)

    return () => clearTimeout(timer)
  }, [open, animationDuration])

  return {
    visible,
    animationState,
    onSwipeStart,
    onSwipeMove,
    onSwipeEnd,
    handleModalClose,
  }
}
